import { Check, CheckCircle, Palette } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { ActivityIndicator } from "react-native";
import { TouchableOpacity } from "react-native-gesture-handler";
import uuid from "react-native-uuid";
import { Circle, Text, View, XStack, YStack } from "tamagui";
import { PlanInfoBox } from "../../../plans/PlanInfoBox";
import { convertTemplateToPlanira } from "../../../templates/convertTemplateToPlanira";
import { useCreatePlanFromTemplate } from "../../../templates/useCreatePlanFromTemplate";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { EmojiPicker } from "../../../ui/EmojiPicker";
import { Input } from "../../../ui/InputNew";
import { Label } from "../../../ui/Label";
import { Subtext } from "../../../ui/Subtext";
import { Title } from "../../../ui/Title";
import { EditorForm } from "../../../ui/YooptaEditor";
import { useUploadCover } from "../../hooks/useUploadCover";
import { TagColorsAvailable } from "../../tags/types";
import { CreateWizardButton } from "./CreateWizardButton";
import { CreateWizardProgress } from "./CreateWizardProgress";
import type { CreateWizardState } from "./CreateWizardState";

export const WizardStepInfo: React.FC<{ state: CreateWizardState }> = observer(
  ({ state }) => {
    const createPlanira = useCreatePlanFromTemplate();

    const [isCreating, setIsCreating] = React.useState(false);

    const [showColors, setShowColors] = React.useState(false);
    const [showEmojiSheet, setSetEmojiSheet] = useState(false);

    const selectCoverButton = useUploadCover(
      () => {},
      async (coverUrl) => {
        return state?.setPlanInfo({ coverUrl });
      },
      uuid.v4(), // TODO - move to after planira is created for new uploads?
    );

    return (
      <YStack gap="$medium">
        <Box backgroundColor={"$primary"} gap={"$small"}>
          <Title>Final step!</Title>
          <Text>Set the info of your Planira and then you're ready</Text>

          <CreateWizardProgress state={state} />
        </Box>

        {isCreating && (
          <View height={250} alignItems="center" justifyContent="center">
            <YStack gap="$medium">
              <ActivityIndicator size={"large"} color={"#ffd84e"} />
              <Body muted>Hold on!</Body>
            </YStack>
          </View>
        )}

        {!isCreating && (
          <>
            <View position={"sticky" as "static"} top={61} zIndex={50}>
              <PlanInfoBox
                info={{
                  ...state.planInfo,
                  title: state.planInfo.title,
                  titlePlaceholder: (
                    <Text color="#fff" opacity={0.5}>
                      Planira title
                    </Text>
                  ),
                }}
                hideEdit
              />
            </View>

            <Box>
              <Label>Planira title</Label>
              <Body>
                Set the title of the Planira to help your group understand the
                purpose
              </Body>
              <Input
                type="border"
                preventNewLine
                placeholder="Planira title"
                onChangeText={(text) => state.setPlanInfo({ title: text })}
              />
            </Box>

            <Box onPress={() => setSetEmojiSheet(true)}>
              <Text>
                <Label>Icon</Label> <Subtext>Optional</Subtext>
              </Text>
              <View borderWidth={1} borderColor="$divider" padding="$medium">
                <Label>{state.planInfo.emoji}</Label>
              </View>
            </Box>

            <Box>
              <Text>
                <Label>Description</Label> <Subtext>Optional</Subtext>
              </Text>
              <EditorForm
                id="description"
                onSave={(data) =>
                  state.setPlanInfo({ descriptionYooptaEditor: data })
                }
              />
            </Box>

            <Box>
              <Text>
                <Label>Background</Label> <Subtext>Optional</Subtext>
              </Text>

              {showColors && (
                <Box>
                  <XStack
                    width={"100%"}
                    flexWrap="wrap"
                    gap="$small"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <Palette size={"$medium"} />
                    {TagColorsAvailable.map((color) => {
                      const isCurrent =
                        color.color === state.planInfo.backgroundColor;

                      return (
                        <TouchableOpacity
                          key={color.color}
                          onPress={() => {
                            state.setPlanInfo({
                              backgroundColor: color.color,
                              coverUrl: null,
                            });
                          }}
                        >
                          <Circle
                            key={color.color}
                            backgroundColor={color.color}
                            size="$medium"
                          >
                            {isCurrent && (
                              <CheckCircle color={color.textColor} />
                            )}
                          </Circle>
                        </TouchableOpacity>
                      );
                    })}
                  </XStack>
                </Box>
              )}

              <XStack
                width={"100%"}
                gap={"$medium"}
                marginTop={"$small"}
                alignItems={"center"}
              >
                <View flex={1}>{selectCoverButton}</View>
                <Label color={"$muted"}>or</Label>

                <View flex={1}>
                  <Button
                    type="secondary"
                    icon={showColors ? Check : Palette}
                    onPress={() => {
                      setShowColors(!showColors);
                    }}
                  >
                    {showColors ? "Close" : "Color"}
                  </Button>
                </View>
              </XStack>
            </Box>

            <CreateWizardButton
              disabled={!state.planInfo.title}
              onPress={async () => {
                setIsCreating(true);
                const { planFlowTemplate, userDisplayName } =
                  state.createSummary;

                const template = convertTemplateToPlanira(planFlowTemplate);

                // Set modules to be kept in creation
                const modulesToCreate =
                  template.sections[1].modules.map((m, i) => ({
                    ...m,
                    keep: true,
                    index: i,
                  })) ?? [];

                await createPlanira(
                  template,
                  modulesToCreate,
                  template.title,
                  userDisplayName,
                );
                setIsCreating(false);
              }}
              position={"sticky" as "static"}
              bottom={0}
              subText={`Let's go ${state.displayName}!`}
            >
              Create Planira
            </CreateWizardButton>
          </>
        )}

        <EmojiPicker
          sheetId="emoji-wizard"
          open={showEmojiSheet}
          onOpenChange={(open: boolean) => setSetEmojiSheet(open)}
          onSelect={(emoji) => {
            state.setPlanInfo({ emoji: emoji.emoji });
            setSetEmojiSheet(false);
          }}
        />
      </YStack>
    );
  },
);
